import { toast } from "react-toastify";
import { handleError } from "../../../../utils/helpers";
import { BaseApiResponse, QueryParams } from "../../../../utils/types";
import { adminBaseApi } from "../../../api";
import {
  AllSalesAgentsResponse,
  AssignUserToAgent,
  CreateSalesAgent,
  SalesAgentEarningsResponse,
  SalesAgentEvents,
  SalesAgentEventsQuery,
  SalesAgentMetrics,
  SalesAgentMonthlyEarnings,
  SalesAgentMonthlyEarningsQuery,
  SalesAgentOrganizersQuery,
  SalesAgentOrganizersResponse,
  SalesPerformanceQuery,
  SalesPerformanceResponse,
  SingleSalesAgentRespnse,
} from "./sales.types";

export const adminSalesApi = adminBaseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    createSalesAgent: builder.mutation<BaseApiResponse, CreateSalesAgent>({
      query: (payload) => {
        return {
          url: `sales`,
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    getSalesAgentDashboardMetrics: builder.query<
      SalesAgentMetrics,
      { id: string }
    >({
      query: (params) => {
        return {
          url: `admin/sales/${params.id}/dashboard-metrics`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getSalesAgentEvents: builder.query<SalesAgentEvents, SalesAgentEventsQuery>(
      {
        query: ({ id, year, month, ...params }) => {
          return {
            url: `admin/sales/${id}/events-table`,
            method: "GET",
            params: { ...params },
          };
        },
        async onQueryStarted(arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (err) {
            handleError(err);
          }
        },
      },
    ),
    getSalesAgentEarningsSummary: builder.query<
      SalesAgentEarningsResponse,
      { id: string }
    >({
      query: (params) => {
        return {
          url: `admin/sales/${params.id}/earnings-summary`,
          method: "GET",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getSalesAgentMonthlyEarnings: builder.query<
      SalesAgentMonthlyEarnings,
      SalesAgentMonthlyEarningsQuery
    >({
      query: ({ id, sortOrder, ...params }) => {
        return {
          url: `admin/sales/${id}/monthly-earnings`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getAllSalesAgents: builder.query<AllSalesAgentsResponse, QueryParams>({
      query: ({ ...params }) => {
        return {
          url: `admin/sales/agents`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getAllSalesPerformance: builder.query<
      SalesPerformanceResponse,
      SalesPerformanceQuery
    >({
      query: ({ ...params }) => {
        return {
          url: `admin/sales/performance`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getSalesAgentOrganizers: builder.query<
      SalesAgentOrganizersResponse,
      SalesAgentOrganizersQuery
    >({
      query: ({ id, ...params }) => {
        return {
          url: `admin/sales/${id}/invited-organizers`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err);
        }
      },
    }),
    getSingleSalesAgent: builder.query<SingleSalesAgentRespnse, { id: string }>(
      {
        query: ({ id, ...params }) => {
          return {
            url: `admin/sales/agent-details/${id}`,
            method: "GET",
            params: params,
          };
        },
        async onQueryStarted(arg, { queryFulfilled }) {
          try {
            await queryFulfilled;
          } catch (err) {
            handleError(err);
          }
        },
      },
    ),
    updateSalesAgent: builder.mutation<
      BaseApiResponse,
      { id: string } & Partial<CreateSalesAgent>
    >({
      query: ({ id, ...payload }) => {
        return {
          url: `admin/sales/agent/${id}`,
          method: "PUT",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    removeSalesAgent: builder.mutation<
      BaseApiResponse,
      { id: string } & Partial<CreateSalesAgent>
    >({
      query: ({ id, ...payload }) => {
        return {
          url: `admin/sales/agent/${id}`,
          method: "DELETE",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
    assignUserToAgent: builder.mutation<BaseApiResponse, AssignUserToAgent>({
      query: ({ ...payload }) => {
        return {
          url: `admin/sales/assign-organizer`,
          method: "POST",
          body: { ...payload },
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          toast.success(data.message);
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
  }),
});

export const {
  useCreateSalesAgentMutation,
  useGetSalesAgentDashboardMetricsQuery,
  useGetSalesAgentEventsQuery,
  useGetSalesAgentEarningsSummaryQuery,
  useGetSalesAgentMonthlyEarningsQuery,
  useGetAllSalesAgentsQuery,
  useGetAllSalesPerformanceQuery,
  useGetSingleSalesAgentQuery,
  useGetSalesAgentOrganizersQuery,
  useUpdateSalesAgentMutation,
  useRemoveSalesAgentMutation,
  useAssignUserToAgentMutation,
} = adminSalesApi;
